import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import BookmarkOutlinedIcon from '@mui/icons-material/BookmarkOutlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { AI_MEDIA_TYPES, CUSTOM_BUCKET_ID } from '../../../constants';
import TimeLabel from '../../time-label/TimeLabel';
import ActionType from '../../action-type/ActionType';
import CheckBadge from '../check-badge/CheckBadge';
import ImageModal from '../image-modal/ImageModal';
import useCommonModal from '../../../hooks/useCommonModal';
import CollapsedButton, {
  COLLAPSED_BUTTON_SIZES,
  COLLAPSED_BUTTON_TYPES,
} from '../../../components/collapsed-button/CollapsedButton';
import downloadFile from '../../../utils/downloadFile';
import { useProject } from '../../../context/ProjectContext';
import mediaRuntimeCache from '../../utils/responsesCache';

import scss from './MediaCard.module.scss';
import FailedMedia from '../../components/failed-media/FailedMedia';

function MediaCard({
  id,
  requestId,
  media,
  mediaType,
  actionType,
  time,
  actionOptions,
  fileName,
  favoriteId,
  isSaved,
  isFavorite,
  additionalCardActions,
}) {
  const { t } = useTranslation('studio');
  const { actions } = useProject();
  const { id: projectId } = useParams();
  const wrapperRef = useRef(null);

  const [loadingTypes, setLoadingTypes] = useState([]);

  const { isOpen, handleOpenModal, handleCloseModal } = useCommonModal({ initialState: false });

  const selectedRequest = useSelector((state) => state.aiStudio.selectedRequest);
  const bucket = useSelector((state) => state.aiStudio.bucket);

  const isCustomBucket = useMemo(() => {
    return bucket?.bucketId === CUSTOM_BUCKET_ID;
  }, [bucket]);

  const isSelected = useMemo(() => {
    return selectedRequest === requestId;
  }, [selectedRequest, requestId]);

  const handleSave = async () => {
    setLoadingTypes((prev) => [...prev, LOADING_TYPES.SAVING_TO_BUCKET]);
    await actions('saveToBucket', { id, requestId, mediaType, actionType, fileName });
    setLoadingTypes((prev) => prev.filter((type) => type !== LOADING_TYPES.SAVING_TO_BUCKET));
  };

  const handleCreateNewBucket = async () => {
    const blob = mediaRuntimeCache.getBlob(id);
    const file = new File([blob], fileName, { type: mediaType });
    setLoadingTypes((prev) => [...prev, LOADING_TYPES.CREATING_NEW_BUCKET]);
    await actions('createNewBucket', { projectId, file });
    setLoadingTypes((prev) => prev.filter((type) => type !== LOADING_TYPES.CREATING_NEW_BUCKET));
  };

  const handleDownload = () => {
    if (media) {
      downloadFile(media, fileName);
    }
  };

  const handleAddToFavorites = async () => {
    setLoadingTypes((prev) => [...prev, LOADING_TYPES.ADDING_TO_FAVORITES]);
    await actions('addToFavorites', { id, projectId, mediaType, actionType, fileName });
    setLoadingTypes((prev) => prev.filter((type) => type !== LOADING_TYPES.ADDING_TO_FAVORITES));
  };

  const handleRemoveFromFavorites = async () => {
    setLoadingTypes((prev) => [...prev, LOADING_TYPES.ADDING_TO_FAVORITES]);
    if (favoriteId) {
      await actions('removeFavoriteMedia', { id: favoriteId, projectId });
    }

    setLoadingTypes((prev) => prev.filter((type) => type !== LOADING_TYPES.ADDING_TO_FAVORITES));
  };

  const scrollIntoView = () => {
    wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  useEffect(() => {
    if (isSelected) {
      scrollIntoView();
    }
  }, [isSelected]);

  return (
    <div className={clsx(scss.card, isSelected && scss.selected)} ref={wrapperRef}>
      {media && mediaType === AI_MEDIA_TYPES.IMAGE && (
        <img
          src={media}
          alt={fileName}
          className={scss.image}
          role='presentation'
          loading='lazy'
          decoding='async'
          onClick={handleOpenModal}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleOpenModal();
            }
          }}
        />
      )}
      {media && mediaType === AI_MEDIA_TYPES.VIDEO && (
        <video src={media} alt={fileName} className={scss.video} controls preload='metadata' loading='lazy'>
          <track kind='captions' />
        </video>
      )}

      {media && (
        <div className={scss.actions}>
          <CollapsedButton
            type={isFavorite ? COLLAPSED_BUTTON_TYPES.SECONDARY : COLLAPSED_BUTTON_TYPES.TABLE_ROW}
            size={COLLAPSED_BUTTON_SIZES.MEDIUM}
            onClick={isFavorite ? handleRemoveFromFavorites : handleAddToFavorites}
            isLoading={loadingTypes.includes(LOADING_TYPES.ADDING_TO_FAVORITES)}
            icon={isFavorite ? BookmarkOutlinedIcon : BookmarkBorderOutlinedIcon}
            label={isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
          />
          <CollapsedButton
            type={COLLAPSED_BUTTON_TYPES.TABLE_ROW}
            size={COLLAPSED_BUTTON_SIZES.MEDIUM}
            onClick={handleDownload}
            icon={DownloadOutlinedIcon}
            label={t('download')}
          />
          {additionalCardActions.map((action) => (
            <CollapsedButton
              key={action.label}
              type={action.type}
              size={action.size}
              onClick={() => action.onClick({ id, requestId, media, mediaType, actionType, fileName, favoriteId })}
              icon={action.icon}
              label={action.label}
              isLoading={action.isLoading}
            />
          ))}
          <CheckBadge isChecked={isSaved} />
          {!isSaved && !isCustomBucket && (
            <CollapsedButton
              type={COLLAPSED_BUTTON_TYPES.PRIMARY}
              size={COLLAPSED_BUTTON_SIZES.MEDIUM}
              onClick={handleSave}
              isLoading={loadingTypes.includes(LOADING_TYPES.SAVING_TO_BUCKET)}
              icon={SaveOutlinedIcon}
              label={t('saveToBucket')}
            />
          )}
          <CollapsedButton
            type={COLLAPSED_BUTTON_TYPES.PRIMARY}
            size={COLLAPSED_BUTTON_SIZES.MEDIUM}
            onClick={handleCreateNewBucket}
            isLoading={loadingTypes.includes(LOADING_TYPES.CREATING_NEW_BUCKET)}
            icon={CreateNewFolderOutlinedIcon}
            label={t('createNewBucket')}
          />
        </div>
      )}
      <div className={scss.footer}>
        {actionType ? <ActionType selectedAction={actionType} actionOptions={actionOptions} isShowGroupLabel /> : null}
        {time ? <TimeLabel time={time} /> : null}
      </div>

      <ImageModal isOpen={isOpen} onClose={handleCloseModal} src={media} alt={fileName} />
    </div>
  );
}

export default MediaCard;

export const LOADING_TYPES = {
  CREATING_NEW_BUCKET: 'CREATING_NEW_BUCKET',
  SAVING_TO_BUCKET: 'SAVING_TO_BUCKET',
  ADDING_TO_FAVORITES: 'ADDING_TO_FAVORITES',
};
